import type { Timeseries } from "../types";
import type { DimensionSeries } from "../config/data";

/**
 * Minimal client for the Mimir / Prometheus HTTP API.
 *
 * Requests go through the `/prometheus` dev-server proxy (see vite.config.ts),
 * which points at the Mimir query frontend and injects the `X-Scope-OrgID`
 * tenant header. Going through the proxy also keeps this same-origin, so no CORS
 * preflight is involved.
 */

/** One series of a `matrix` result: `[unixSeconds, value]` samples. */
export interface PromMatrixSeries {
  metric: Record<string, string>;
  values: [number, string][];
}

interface PromQueryRangeResponse {
  status: "success" | "error";
  errorType?: string;
  error?: string;
  data?: {
    resultType: string;
    result: PromMatrixSeries[];
  };
}

/** Bound the wait so a silent endpoint surfaces an error instead of hanging. */
const REQUEST_TIMEOUT_MS = 30_000;

/** Longest snippet of a non-JSON error body worth showing in a chart. */
const MAX_BODY_SNIPPET = 160;

/**
 * Turn an error response body into one readable sentence.
 *
 * An environment without a route to Prometheus answers with whatever sits at
 * that path instead — for the static production build that is the dashboard's
 * own index.html. Passing such a body through verbatim would render a wall of
 * markup, so HTML is reported as a routing problem rather than quoted.
 */
function describeErrorBody(body: string, contentType: string | null): string {
  const trimmed = body.trim();

  if (contentType?.includes("text/html") || /^<(!doctype|html)\b/i.test(trimmed)) {
    return "The endpoint returned a web page instead of JSON, so Prometheus is most likely not reachable from this environment.";
  }

  if (!trimmed) return "The response body was empty.";

  return trimmed.length > MAX_BODY_SNIPPET ? `${trimmed.slice(0, MAX_BODY_SNIPPET)}…` : trimmed;
}

/**
 * Run a range query. `start`/`end` are Dates, `stepSeconds` the resolution.
 * Returns the matrix series; an empty array when nothing matched.
 */
export async function runPromQueryRange(
  query: string,
  start: Date,
  end: Date,
  stepSeconds: number,
): Promise<PromMatrixSeries[]> {
  const params = new URLSearchParams({
    query,
    start: String(Math.floor(start.getTime() / 1000)),
    end: String(Math.floor(end.getTime() / 1000)),
    step: String(stepSeconds),
  });

  let response: Response;
  try {
    response = await fetch(`/prometheus/api/v1/query_range?${params.toString()}`, {
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  } catch (cause) {
    // Neither a timeout nor a transport failure produces a Response, so both
    // land here. Without this the chart would sit on its loading skeleton.
    if (cause instanceof DOMException && cause.name === "TimeoutError") {
      throw new Error(`No response within ${REQUEST_TIMEOUT_MS / 1000}s.`);
    }
    throw new Error("Could not reach the Prometheus endpoint.");
  }

  if (!response.ok) {
    const detail = describeErrorBody(await response.text(), response.headers.get("content-type"));
    throw new Error(`Query failed with HTTP ${response.status}. ${detail}`);
  }

  const body: PromQueryRangeResponse = await response.json();

  if (body.status !== "success") {
    throw new Error(`Prometheus error (${body.errorType ?? "unknown"}): ${body.error ?? "no detail"}`);
  }

  if (!body.data) return [];

  if (body.data.resultType !== "matrix") {
    throw new Error(`Expected a matrix result, got "${body.data.resultType}" — use a range query.`);
  }

  return body.data.result;
}

/**
 * Turn a matrix result into the `DimensionSeries` shape the charts consume,
 * keyed by the value of `splitLabel`.
 *
 * `scale` is applied to every sample — pass 100 to render a ratio as a percentage.
 * Prometheus returns sample values as strings, so they always go through Number().
 * Samples that come back as NaN (Prometheus' encoding for e.g. 0/0) become null so
 * the chart shows a gap rather than dropping to zero.
 */
export function matrixToDimensionSeries(series: PromMatrixSeries[], splitLabel: string, scale = 1): DimensionSeries {
  const result: DimensionSeries = {};

  for (const [index, entry] of series.entries()) {
    // A `sum(...) by (label)` result carries only the grouping label. Fall back to
    // a positional key so an unlabelled series is still plotted instead of colliding.
    const key = entry.metric[splitLabel] ?? `series ${index + 1}`;

    const timeseries: Timeseries = {
      timestamps: entry.values.map(([seconds]) => new Date(seconds * 1000)),
      data: entry.values.map(([, value]) => {
        const parsed = Number(value) * scale;
        return Number.isFinite(parsed) ? parsed : null;
      }),
    };

    result[key] = timeseries;
  }

  return result;
}
